import React from 'react';
import { cn } from '../utils';

export interface AlertProps {
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const defaultIcons = {
  info: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      className="stroke-current shrink-0 w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      ></path>
    </svg>
  ),
  success: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="stroke-current shrink-0 h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  warning: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="stroke-current shrink-0 h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
      />
    </svg>
  ),
  error: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="stroke-current shrink-0 h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

export const Alert: React.FC<AlertProps> = ({
  type = 'info',
  title,
  message,
  children,
  icon,
  action,
  dismissible,
  onDismiss,
  className,
}) => {
  const alertClasses = cn(
    'alert',
    type === 'info' && 'alert-info',
    type === 'success' && 'alert-success',
    type === 'warning' && 'alert-warning',
    type === 'error' && 'alert-error',
    className
  );

  // Use children if provided, otherwise fall back to message prop
  const content = children || (message && <div className="text-sm">{message}</div>);

  return (
    <div role="alert" className={alertClasses}>
      {icon !== null && (icon || defaultIcons[type])}
      <div className="flex-1">
        {title && <h3 className="font-bold">{title}</h3>}
        {content}
      </div>
      {action && (
        <button className="btn btn-sm" onClick={action.onClick}>
          {action.label}
        </button>
      )}
      {dismissible && (
        <button className="btn btn-sm btn-ghost" onClick={onDismiss} aria-label="Dismiss alert">
          ✕
        </button>
      )}
    </div>
  );
};
